import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiFilter, FiGrid, FiList, FiX } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ProductCard, Product } from '../components/ProductCard';
import { getActiveProducts, FrontendProduct } from '../api/productApi';
import PriceRangeSlider from '../components/PriceRangeSlider';
import CategoryFilter, { standardCategories, categorySizeTypes } from '../components/CategoryFilter';

const AllGrailsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleProductCount, setVisibleProductCount] = useState(12);
  const [activeFilter, setActiveFilter] = useState('grails');
  const [selectedBrand, setSelectedBrand] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSize, setSelectedSize] = useState("All");
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(15000);
  const containerRef = useRef<HTMLDivElement>(null);
  const unmountingRef = useRef(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  // Filter options - kept static as these are logical groupings
  const filters = [
    { id: 'all', label: 'All' },
    { id: 'grails', label: 'Grails' },
    { id: 'new', label: 'New Arrivals' }
  ];
  
  // Generate brand options dynamically based on available products
  const generateBrandOptions = () => {
    // Always include "All Brands" option
    const options = [{ id: 'All', label: 'All Brands' }];
    
    // Get unique brands from products
    const uniqueBrands = Array.from(new Set(products.map(product => product.brand)));
    
    // Sort brands alphabetically
    uniqueBrands.sort();
    
    // Add each unique brand to options
    uniqueBrands.forEach(brand => {
      if (brand && brand.trim() !== '') {
        options.push({ id: brand, label: brand });
      }
    });
    
    return options;
  };
  
  // Generate brand options based on available products
  const brands = generateBrandOptions();
  
  // Size category options - using standardCategories from CategoryFilter
  // const categories = [
  //   { id: 'All', label: 'All Categories' },
  //   { id: 'M', label: "Men's (M)" },
  //   { id: 'W', label: "Women's (W)" },
  //   { id: 'TD', label: 'Toddlers (TD)' },
  //   { id: 'GS', label: 'Grade School (GS)' }
  // ];
  
  // Get dynamic size options based on current category and available products
  const getDynamicSizeOptions = (category: string) => {
    const options = ['All']; // Always include "All" option
    
    console.log(`Getting size options for category: ${category}`);
    
    // Filter products by the selected category
    const filteredProducts = category === 'All' 
      ? products 
      : products.filter(product => {
          // Check if the category matches the product's sizeType
          const match = (product.sizeType && product.sizeType.includes(category)) ||
                      (product.category === category);
          
          if (!match) {
            console.log(`Product ${product.id} excluded from size options - need: ${category}, has sizeType: ${product.sizeType}, category: ${product.category}`);
          } else {
            console.log(`Product ${product.id} included for size options - matches category: ${category}`);
          }
          
          return match;
        });
    
    console.log(`Found ${filteredProducts.length} products matching category ${category}`);
    
    // Get all available sizes from filtered products
    const allSizes: string[] = [];
    const numericSizesMap: { [key: string]: string } = {}; // To track numeric sizes and their original formats
    
    filteredProducts.forEach(product => {
      console.log(`Processing sizes for product ${product.id}: ${JSON.stringify(product.sizes)}`);
      
      product.sizes.forEach(size => {
        // Add the original size format
        if (!allSizes.includes(size)) {
          allSizes.push(size);
          console.log(`Added size ${size} to available sizes`);
          
          // Track numeric representation for potential deduplication
          if (size.match(/[0-9]/)) {
            let numericPart = "";
            
            // Extract the numeric part of the size
            if (size.includes('M')) {
              numericPart = size.replace('M', '');
            } else if (size.includes('Y')) {
              numericPart = size.replace('Y', '');
            } else if (size.includes('W')) {
              numericPart = size.replace('W', '');
            } else if (!isNaN(parseFloat(size))) {
              numericPart = size;
            }
            
            // If we have a clear numeric part, remember the original format
            if (numericPart && !isNaN(parseFloat(numericPart))) {
              numericSizesMap[numericPart] = size;
            }
          }
        }
      });
    });
    
    // Return early with just "All" if no sizes found
    if (allSizes.length === 0) {
      console.log(`No sizes found for category ${category}, returning only "All" option`);
      return options;
    }
    
    // For categories like Men's, prioritize numeric sizes (e.g., "9" over "9M")
    if (category === 'M') {
      // Add numeric sizes without suffix if not already present
      Object.keys(numericSizesMap).forEach(numericSize => {
        const originalSize = numericSizesMap[numericSize];
        if (originalSize.includes('M') && !allSizes.includes(numericSize)) {
          allSizes.push(numericSize);
        }
      });
    }
    
    // For GS category, prioritize numeric sizes (e.g., "4" over "4Y")
    if (category === 'GS') {
      // Add numeric sizes without suffix if not already present
      Object.keys(numericSizesMap).forEach(numericSize => {
        const originalSize = numericSizesMap[numericSize];
        if (originalSize.includes('Y') && !allSizes.includes(numericSize)) {
          allSizes.push(numericSize);
        }
      });
    }
    
    // Sort sizes numerically if possible
    allSizes.sort((a, b) => {
      // If sizes contain letters, use a more complex sorting
      if (a.match(/[a-zA-Z]/) || b.match(/[a-zA-Z]/)) {
        // Extract numeric parts for comparison
        const numA = parseFloat(a.replace(/[a-zA-Z]/g, ''));
        const numB = parseFloat(b.replace(/[a-zA-Z]/g, ''));
        
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      }
      // Otherwise, sort numerically
      return parseFloat(a) - parseFloat(b);
    });
    
    return [...options, ...allSizes];
  };
  
  // Generate dynamic price ranges based on available products
  const generatePriceRanges = () => {
    // Always include "All Prices" option
    const options = [{ id: 'All', label: 'All Prices' }];
    
    // Standard price ranges
    const standardRanges = [
      { id: '0-100', label: 'Under $100' },
      { id: '100-200', label: '$100 - $200' },
      { id: '200-300', label: '$200 - $300' },
      { id: '300-500', label: '$300 - $500' },
      { id: '500+', label: 'Over $500' }
    ];
    
    // Only include price ranges that have products
    standardRanges.forEach(range => {
      const [min, max] = range.id.split('-').map(Number);
      const hasProductsInRange = products.some(product => {
        if (!max) {
          // Handle "500+" case
          return product.price >= min;
        }
        return product.price >= min && product.price <= max;
      });
      
      if (hasProductsInRange) {
        options.push(range);
      }
    });
    
    return options;
  };
  
  // Generate price range options based on available products
  const priceRanges = generatePriceRanges();
  
  useEffect(() => {
    return () => {
      // Mark component as unmounting to prevent state updates
      unmountingRef.current = true;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // Use getActiveProducts from the API instead of directly fetching
        const data = await getActiveProducts();
        
        // Don't update state if component is unmounting
        if (!isMounted || unmountingRef.current) return;
        
        // Transform FrontendProduct to Product type if needed
        const formattedProducts: Product[] = data.map(product => {
          // Create a sizeDisplay if it's missing
          let sizeDisplay = product.sizeDisplay;
          if (!sizeDisplay && product.size) {
            const sizeType = product.sizeType || '';
            if (sizeType === 'M' || sizeType === 'W') {
              sizeDisplay = `${product.size}${sizeType}`;
            } else if (sizeType === 'TD' || sizeType === 'GS' || sizeType.includes('Y') || sizeType.includes('C')) {
              sizeDisplay = `${product.size} (${sizeType})`;
            } else {
              // If sizeType is unknown but we can infer from category
              const category = product.sizeType?.includes('M') && !product.sizeType?.includes('W') ? 'M' : 
                       product.sizeType?.includes('W') ? 'W' : 
                       product.sizeType?.includes('GS') || product.sizeType?.includes('Y') ? 'GS' : 
                       product.sizeType?.includes('TD') || product.sizeType?.includes('C') ? 'TD' : 'All';
              sizeDisplay = category === 'M' || category === 'W' ? 
                `${product.size}${category}` : 
                category !== 'All' ? `${product.size} (${category})` : product.size;
            }
          }
          
          return {
            id: product.id,
            name: product.name,
            brand: product.brand,
            price: product.price,
            image: product.image,
            images: product.images || (product.image ? [product.image] : []),
            sizes: product.sizes || [],
            category: product.sizeType?.includes('M') && !product.sizeType?.includes('W') ? 'M' : 
                     product.sizeType?.includes('W') ? 'W' : 
                     product.sizeType?.includes('GS') || product.sizeType?.includes('Y') ? 'GS' : 
                     product.sizeType?.includes('TD') || product.sizeType?.includes('C') ? 'TD' : 'All',
            condition: product.condition,
            collab: product.collab,
            sizeDisplay,
            sizeType: product.sizeType
          };
        });
        
        if (isMounted && !unmountingRef.current) {
          setProducts(formattedProducts);
          setLoading(false);
        }
      } catch (e) {
        console.error("Error fetching products:", e);
        if (isMounted && !unmountingRef.current) {
          setError('Failed to load products');
          setLoading(false);
        }
      }
    };
    
    fetchProducts();
    
    return () => {
      isMounted = false;
      setLoading(false); // Ensure loading state is reset
    };
  }, []);
  
  // Add cleanup handler for transitions
  useEffect(() => {
    const handleBeforeNavigate = () => {
      unmountingRef.current = true;
      setLoading(false);
    };
    
    window.addEventListener('beforeunload', handleBeforeNavigate);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeNavigate);
    };
  }, []);
  
  // Reset the size when category changes
  useEffect(() => {
    setSelectedSize('All');
  }, [selectedCategory]);
  
  // Helper function to normalize size display for UI consistency
  const normalizeSizeDisplay = (size: string): string => {
    // If the size is numeric only (e.g., "9"), check the current category
    if (!isNaN(parseFloat(size)) && !size.match(/[a-zA-Z]/)) {
      // Add category suffix based on the current selection
      if (selectedCategory === 'M') return `${size}M`;
      if (selectedCategory === 'W') return `${size}W`;
      if (selectedCategory === 'GS') return `${size}Y`;
      // Default to Men's if no category is selected
      return selectedCategory === 'All' ? `${size}M` : `${size}M`;
    }
    return size;
  };

  // Get current applicable sizes based on category
  const getCurrentSizes = () => {
    // Add debug logging to see what's happening
    console.log("Current Category:", selectedCategory);
    console.log("Category Size Type:", categorySizeTypes[selectedCategory]);
    return getDynamicSizeOptions(selectedCategory);
  };
  
  // Helper function to strip suffixes for dropdown selection
  const stripSizeSuffix = (size: string): string => {
    // Strip M suffix for men's sizes
    if (size.includes('M')) {
      const numPart = parseFloat(size.replace('M', ''));
      if (!isNaN(numPart)) return numPart.toString();
    }
    
    // Strip Y suffix for GS sizes
    if (size.includes('Y')) {
      const numPart = parseFloat(size.replace('Y', ''));
      if (!isNaN(numPart)) return numPart.toString();
    }
    
    // Strip W suffix for women's sizes
    if (size.includes('W')) {
      const numPart = parseFloat(size.replace('W', ''));
      if (!isNaN(numPart)) return numPart.toString();
    }
    
    return size;
  };
  
  // Apply filters to products
  const filteredProducts = products.filter(product => {
    // Filter by brand
    if (selectedBrand !== 'All' && product.brand !== selectedBrand) {
      return false;
    }
    
    // Filter by category (size type)
    if (selectedCategory !== 'All') {
      // The category is now directly the sizeType (M, W, GS, TD)
      if (!product.sizeType || !product.sizeType.includes(selectedCategory)) {
        console.log(`Product ${product.id} filtered out by category - need: ${selectedCategory}, has: ${product.sizeType}`);
        return false;
      }
    }
    
    // Filter by specific size
    if (selectedSize !== 'All') {
      // Normalize the size format for comparison based on current category
      const normalizedSelectedSize = normalizeSizeDisplay(selectedSize);
      
      // Log the normalized size for debugging
      console.log(`Filtering with normalized size: ${normalizedSelectedSize} (original: ${selectedSize})`);
      
      const sizeMatches = product.sizes.some(productSize => {
        // Exact match with size as-is
        if (productSize === selectedSize) {
          console.log(`Exact match found: ${productSize} matches ${selectedSize}`);
          return true;
        }
        
        // Match with normalized size (includes category suffix)
        if (productSize === normalizedSelectedSize) {
          console.log(`Normalized match found: ${productSize} matches ${normalizedSelectedSize}`);
          return true;
        }
        
        // Strip suffixes for comparison to handle category-agnostic numeric sizes
        const strippedSelectedSize = stripSizeSuffix(selectedSize);
        const strippedProductSize = stripSizeSuffix(productSize);
        
        // Compare stripped sizes (numeric parts only)
        if (strippedSelectedSize === strippedProductSize) {
          console.log(`Stripped match found: ${strippedSelectedSize} = ${strippedProductSize}`);
          return true;
        }
        
        // Additional checks for categorical matches (e.g., 9M should match 9 when category is M)
        // For Men's sizes
        if (selectedCategory === 'M') {
          // If selected size is "9" and product size is "9M" or vice versa
          if (selectedSize.includes('M') && !productSize.includes('M')) {
            const sizeNum = parseFloat(selectedSize.replace('M', ''));
            const productSizeNum = parseFloat(productSize);
            if (!isNaN(productSizeNum) && sizeNum === productSizeNum) {
              console.log(`Men's category match: ${sizeNum}M = ${productSizeNum}`);
              return true;
            }
          } else if (!selectedSize.includes('M') && productSize.includes('M')) {
            const sizeNum = parseFloat(selectedSize);
            const productSizeNum = parseFloat(productSize.replace('M', ''));
            if (!isNaN(sizeNum) && sizeNum === productSizeNum) {
              console.log(`Men's category match: ${sizeNum} = ${productSizeNum}M`);
              return true;
            }
          }
        }
        
        // Similar logic for Women's sizes
        if (selectedCategory === 'W') {
          if (selectedSize.includes('W') && !productSize.includes('W')) {
            const sizeNum = parseFloat(selectedSize.replace('W', ''));
            const productSizeNum = parseFloat(productSize);
            if (!isNaN(productSizeNum) && sizeNum === productSizeNum) {
              console.log(`Women's category match: ${sizeNum}W = ${productSizeNum}`);
              return true;
            }
          } else if (!selectedSize.includes('W') && productSize.includes('W')) {
            const sizeNum = parseFloat(selectedSize);
            const productSizeNum = parseFloat(productSize.replace('W', ''));
            if (!isNaN(sizeNum) && sizeNum === productSizeNum) {
              console.log(`Women's category match: ${sizeNum} = ${productSizeNum}W`);
              return true;
            }
          }
        }
        
        // Similar logic for Grade School sizes
        if (selectedCategory === 'GS') {
          if (selectedSize.includes('Y') && !productSize.includes('Y')) {
            const sizeNum = parseFloat(selectedSize.replace('Y', ''));
            const productSizeNum = parseFloat(productSize);
            if (!isNaN(productSizeNum) && sizeNum === productSizeNum) {
              console.log(`GS category match: ${sizeNum}Y = ${productSizeNum}`);
              return true;
            }
          } else if (!selectedSize.includes('Y') && productSize.includes('Y')) {
            const sizeNum = parseFloat(selectedSize);
            const productSizeNum = parseFloat(productSize.replace('Y', ''));
            if (!isNaN(sizeNum) && sizeNum === productSizeNum) {
              console.log(`GS category match: ${sizeNum} = ${productSizeNum}Y`);
              return true;
            }
          }
        }
        
        return false;
      });
      
      if (!sizeMatches) {
        console.log(`Product ${product.id} filtered out - size doesn't match`);
        return false;
      }
    }
    
    // Filter by price range
    if (minPrice > 0 || maxPrice < 15000) {
      if (product.price < minPrice || product.price > maxPrice) return false;
    }
    
    // Filter by type
    if (activeFilter === 'grails') {
      // Assuming grails are products with collab or price >= 500
      return (product.collab && product.collab.trim() !== '') || product.price >= 500;
    } else if (activeFilter === 'new') {
      // Placeholder logic for new arrivals
      // In a real app, you would use createdAt date or a specific flag
      return true;
    }
    
    return true;
  });
  
  const handleLoadMore = () => {
    setVisibleProductCount(prev => prev + 8);
  };
  
  const handleFilterChange = (filterId: string) => {
    setActiveFilter(filterId);
  };
  
  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBrand(e.target.value);
  };
  
  const handleCategoryChange = (value: string) => {
    console.log(`Changing category to: ${value}`);
    setSelectedCategory(value);
    
    // When category changes, we should reset size selection
    setSelectedSize('All');
    
    // Update URL if needed
    const searchParams = new URLSearchParams(window.location.search);
    if (value !== 'All') {
      searchParams.set('category', value);
    } else {
      searchParams.delete('category');
    }
    
    // Preserve other parameters like brand
    const newUrl = searchParams.toString() 
      ? `/all-heat?${searchParams.toString()}` 
      : '/all-heat';
    window.history.pushState({}, '', newUrl);
    
    // Log available sizes for new category
    const sizesForCategory = getDynamicSizeOptions(value);
    console.log(`Available sizes for category ${value}:`, sizesForCategory);
  };
  
  const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = e.target.value;
    
    // Always allow setting the size regardless of other filters
    setSelectedSize(newSize);
    
    // Update URL parameters if a specific size is selected
    if (newSize !== 'All') {
      // Always apply the current category to the size for consistency
      let sizeValue = newSize;
      
      // Convert numeric size to proper format with suffix
      if (!sizeValue.match(/[a-zA-Z]/) && !isNaN(parseFloat(sizeValue))) {
        if (selectedCategory === 'M') {
          sizeValue = `${sizeValue}M`;
        } else if (selectedCategory === 'GS') {
          sizeValue = `${sizeValue}Y`;
        } else if (selectedCategory === 'W') {
          sizeValue = `${sizeValue}W`;
        } else {
          // Default to Men's if no specific category is selected
          sizeValue = `${sizeValue}M`;
        }
      }
      
      // Preserve existing query parameters
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sizes', sizeValue);
      
      const newUrl = `/all-heat?${searchParams.toString()}`;
      window.history.pushState({}, '', newUrl);
    } else {
      // Remove only the sizes parameter, keep other parameters
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('sizes');
      
      const newUrl = searchParams.toString() 
        ? `/all-heat?${searchParams.toString()}` 
        : '/all-heat';
      window.history.pushState({}, '', newUrl);
    }
  };
  
  const handlePriceChange = (min: number, max: number) => {
    setMinPrice(min);
    setMaxPrice(max);
  };

  // Refresh filter options whenever products change
  useEffect(() => {
    // Only update if we have products and we're not in a loading state
    if (products.length > 0 && !loading) {
      // Get the available brands based on current products
      generateBrandOptions();
      
      // Get available price ranges based on current products
      generatePriceRanges();
    }
  }, [products, loading]);

  // Force scroll to top on component mount
  useEffect(() => {
    // Immediately scroll to top with no animation to ensure the page starts at the top
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-dark text-primary">
      <Header />
      
      <main className="py-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold">All Heat 🔥</h1>
          <p className="text-secondary mt-2">Browse our collection of premium collaborations and high-value sneakers</p>
          
          {/* Display active filters section */}
          {(selectedBrand !== 'All' || selectedCategory !== 'All' || selectedSize !== 'All' || (minPrice > 0 || maxPrice < 15000)) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedBrand !== 'All' && (
                <div className="inline-flex items-center py-2 px-4 bg-accent/10 text-accent rounded-full text-sm font-medium">
                  <span className="mr-2">Brand: {selectedBrand}</span>
                  <button 
                    onClick={() => {
                      setSelectedBrand('All');
                    }}
                    className="text-accent hover:text-white"
                    aria-label="Clear brand filter"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              {selectedCategory !== 'All' && (
                <div className="inline-flex items-center py-2 px-4 bg-accent/10 text-accent rounded-full text-sm font-medium">
                  <span className="mr-2">Category: {standardCategories.find(cat => cat.id === selectedCategory)?.label.replace(' (M)', '').replace(' (W)', '').replace(' (GS)', '').replace(' (TD)', '') || selectedCategory}</span>
                  <button 
                    onClick={() => {
                      setSelectedCategory('All');
                      // Reset size when category is cleared
                      setSelectedSize('All');
                    }}
                    className="text-accent hover:text-white"
                    aria-label="Clear category filter"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              {selectedSize !== 'All' && (
                <div className="inline-flex items-center py-2 px-4 bg-accent/10 text-accent rounded-full text-sm font-medium">
                  <span className="mr-2">Size: {normalizeSizeDisplay(selectedSize)}</span>
                  <button 
                    onClick={() => {
                      setSelectedSize('All');
                      // Clear size URL parameter
                      const searchParams = new URLSearchParams(window.location.search);
                      searchParams.delete('sizes');
                      const newUrl = searchParams.toString() 
                        ? `/all-heat?${searchParams.toString()}` 
                        : '/all-heat';
                      window.history.pushState({}, '', newUrl);
                    }}
                    className="text-accent hover:text-white"
                    aria-label="Clear size filter"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              {(minPrice > 0 || maxPrice < 15000) && (
                <div className="inline-flex items-center py-2 px-4 bg-accent/10 text-accent rounded-full text-sm font-medium">
                  <span className="mr-2">Price: ${minPrice} - ${maxPrice}</span>
                  <button 
                    onClick={() => {
                      setMinPrice(0);
                      setMaxPrice(15000);
                    }}
                    className="text-accent hover:text-white"
                    aria-label="Clear price filter"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              <button
                onClick={() => {
                  setSelectedBrand('All');
                  setSelectedCategory('All');
                  setSelectedSize('All');
                  setMinPrice(0);
                  setMaxPrice(15000);
                  // Clear URL parameters
                  window.history.pushState({}, '', '/all-heat');
                }}
                className="inline-flex items-center py-2 px-4 bg-red-500/10 text-red-400 rounded-full text-sm font-medium hover:bg-red-500/20"
                aria-label="Clear all filters"
              >
                <span>Clear All</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Mobile filter button - only visible on small screens */}
        <div className="sm:hidden mb-6">
          <button 
            onClick={() => setMobileFiltersOpen(true)}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-background-light rounded-md hover:bg-background-light/80 transition-colors"
          >
            <FiFilter className="w-4 h-4" />
            <span>Filters</span>
          </button>
        </div>
        
        {/* Filter and product section */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filter section - desktop */}
          <aside className="hidden sm:block lg:col-span-1 space-y-6">
            <div className="bg-background p-6 rounded-lg">
              <h2 className="text-lg font-medium mb-4">Filters</h2>
              
              {/* Brand filter */}
              <div className="mb-6">
                <label htmlFor="brand" className="block text-sm font-medium mb-2">Brand</label>
                <select
                  id="brand"
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  className="w-full bg-background-light border border-gray-700 rounded-md py-2 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent"
                >
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Category filter */}
              <div className="mb-6">
                <CategoryFilter
                  id="category"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  categories={standardCategories}
                  label="Size Category"
                />
              </div>
              
              {/* Size filter */}
              <div className="mb-6">
                <label htmlFor="size" className="block text-sm font-medium mb-2">Size</label>
                <select
                  id="size"
                  value={selectedSize}
                  onChange={handleSizeChange}
                  className="w-full bg-background-light border border-gray-700 rounded-md py-2 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent"
                >
                  {getCurrentSizes().map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              
              {/* Price range filter */}
              <div>
                <label className="block text-sm font-medium mb-2">Price</label>
                <PriceRangeSlider
                  minValue={0}
                  maxValue={15000}
                  step={50}
                  onChange={handlePriceChange}
                  defaultMinPrice={minPrice}
                  defaultMaxPrice={maxPrice}
                />
              </div>
            </div>
          </aside>
          
          {/* Mobile filter drawer */}
          {mobileFiltersOpen && (
            <div className="fixed inset-0 flex z-40 sm:hidden" aria-modal="true">
              {/* Backdrop */}
              <div 
                className="fixed inset-0 bg-black/70 transition-opacity"
                aria-hidden="true"
                onClick={() => setMobileFiltersOpen(false)}
              ></div>
              
              {/* Sliding panel */}
              <div className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-background px-4 py-6 shadow-xl">
                {/* Close button */}
                <div className="flex items-center justify-between mb-6 px-2">
                  <h2 className="text-lg font-medium">Filters</h2>
                  <button
                    type="button"
                    className="p-2 text-secondary hover:text-primary"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <FiX className="h-6 w-6" />
                  </button>
                </div>
                
                {/* Filter content - mobile */}
                <div className="space-y-6">
                  {/* Brand filter */}
                  <div>
                    <label htmlFor="mobile-brand" className="block text-sm font-medium mb-2">Brand</label>
                    <select
                      id="mobile-brand"
                      value={selectedBrand}
                      onChange={handleBrandChange}
                      className="w-full bg-background-light border border-gray-700 rounded-md py-3 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent"
                    >
                      {brands.map(brand => (
                        <option key={brand.id} value={brand.id}>{brand.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Category filter */}
                  <div>
                    <CategoryFilter
                      id="mobile-category"
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      categories={standardCategories}
                      label="Size Category"
                      className="mb-2"
                    />
                  </div>
                  
                  {/* Size filter */}
                  <div>
                    <label htmlFor="mobile-size" className="block text-sm font-medium mb-2">Size</label>
                    <select
                      id="mobile-size"
                      value={selectedSize}
                      onChange={handleSizeChange}
                      className="w-full bg-background-light border border-gray-700 rounded-md py-3 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent"
                    >
                      {getCurrentSizes().map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Price range filter */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Price</label>
                    <PriceRangeSlider
                      minValue={0}
                      maxValue={15000}
                      step={50}
                      onChange={handlePriceChange}
                      defaultMinPrice={minPrice}
                      defaultMaxPrice={maxPrice}
                    />
                  </div>
                  
                  {/* Apply filters button */}
                  <div className="mt-8">
                    <button
                      className="w-full bg-accent text-white py-3 px-4 rounded-md hover:bg-accent/90 transition-colors"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Products grid */}
          <div className="lg:col-span-3">
            {/* Filter tabs */}
            <div className="mb-6 flex flex-wrap gap-2 items-center overflow-x-auto pb-1">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => handleFilterChange(filter.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    activeFilter === filter.id 
                      ? 'bg-accent text-white' 
                      : 'bg-background-light text-secondary hover:text-primary'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
              
              {/* Applied filters summary */}
              {(selectedBrand !== 'All' || selectedCategory !== 'All' || selectedSize !== 'All' || (minPrice > 0 || maxPrice < 15000)) && (
                <div className="text-sm text-secondary ml-auto">
                  <span className="hidden sm:inline">Filters: </span>
                  {selectedBrand !== 'All' && <span className="ml-1">{selectedBrand}</span>}
                  {selectedCategory !== 'All' && <span className="ml-1">{selectedCategory}</span>}
                  {selectedSize !== 'All' && <span className="ml-1">Size {selectedSize}</span>}
                  {minPrice > 0 && <span className="ml-1">${minPrice} - ${maxPrice}</span>}
                </div>
              )}
            </div>
            
            {/* Loading state */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent mb-4"></div>
                <p className="text-secondary">Loading products...</p>
              </div>
            )}
            
            {/* Error state */}
            {error && (
              <div className="bg-red-900/20 rounded-lg p-8 text-center my-8">
                <div className="text-red-400 mb-3">{error}</div>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-background-light hover:bg-accent text-white rounded-md transition-colors"
                >
                  Retry
                </button>
              </div>
            )}
            
            {/* No results */}
            {!loading && !error && filteredProducts.length === 0 && (
              <div className="bg-background-light/20 rounded-lg p-8 text-center my-8">
                <div className="text-secondary mb-3">No products found matching your filters.</div>
                <button
                  onClick={() => {
                    setSelectedBrand('All');
                    setSelectedCategory('All');
                    setSelectedSize('All');
                    setMinPrice(0);
                    setMaxPrice(15000);
                    setActiveFilter('all');
                  }}
                  className="px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-md transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            )}
            
            {/* Products grid */}
            {!loading && !error && filteredProducts.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredProducts.slice(0, visibleProductCount).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
            
            {/* Load more button */}
            {!loading && !error && filteredProducts.length > visibleProductCount && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-3 bg-background-light hover:bg-background text-primary rounded-md transition-colors"
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AllGrailsPage; 